<template>
  <aside class="terminal-dock" :class="[`is-${phase}`, { collapsed }]" aria-live="polite">
    <button class="dock-header" type="button" @click="collapsed = !collapsed">
      <span class="terminal-icon" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
      <span class="terminal-heading">
        <strong>Rock5A 多模态终端</strong>
        <small>{{ endpointLabel }}</small>
      </span>
      <span class="phase-badge"><i></i>{{ phaseLabel }}</span>
      <span class="collapse-mark" aria-hidden="true">{{ collapsed ? '＋' : '－' }}</span>
    </button>

    <transition name="dock-expand">
      <div v-if="!collapsed" class="dock-body">
        <div class="capability-grid">
          <div class="capability" :class="{ healthy: gestureHealthy, active: gestureEnabled }">
            <span class="capability-dot"></span>
            <div>
              <strong>手势</strong>
              <small>{{ gestureLabel }}</small>
            </div>
          </div>
          <div class="capability" :class="{ healthy: voiceHealthy, active: audioActive }">
            <span class="capability-dot"></span>
            <div>
              <strong>语音</strong>
              <small>{{ voiceLabel }}</small>
            </div>
          </div>
          <div class="capability" :class="{ healthy: terminalFresh }">
            <span class="capability-dot"></span>
            <div>
              <strong>终端</strong>
              <small>{{ terminalLabel }}</small>
            </div>
          </div>
        </div>

        <div v-if="connected" class="metric-row">
          <span>相机 <b>{{ cameraLabel }}</b></span>
          <span>识别 <b>{{ gestureFps }}</b></span>
          <span>NPU <b>{{ npuLabel }}</b></span>
          <span>运行 <b>{{ uptimeLabel }}</b></span>
        </div>

        <div class="build-version" title="前端构建版本与 Git 提交">
          前端 {{ buildInfo.appVersion || buildInfo.version || 'unknown' }} · {{ shortBuildCommit }}
        </div>

        <p class="interaction-tip">
          <span>竖拇指激活</span>
          <span>捏合单击</span>
          <span>握拳唤醒语音</span>
        </p>

        <div v-if="!connected" class="offline-actions">
          <span>{{ errorLabel }}</span>
          <button type="button" :disabled="discoveryState === 'searching'" @click="$emit('retry')">
            {{ discoveryState === 'searching' ? '正在发现…' : '重新发现终端' }}
          </button>
        </div>
      </div>
    </transition>
  </aside>
</template>

<script>
export default {
  name: 'TerminalStatusDock',
  props: {
    status: {
      type: Object,
      default: () => ({}),
    },
    discoveryState: {
      type: String,
      default: 'idle',
    },
    discoveryError: {
      type: String,
      default: '',
    },
    buildInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return { collapsed: true }
  },
  computed: {
    connected() {
      return this.status.control === 'connected'
        && Boolean(this.freshness.control && this.freshness.control.fresh)
    },
    terminal() {
      return this.status.terminalStatus || {}
    },
    freshness() {
      return this.status.freshness || {}
    },
    terminalFresh() {
      return Boolean(this.connected && this.freshness.terminalStatus && this.freshness.terminalStatus.fresh)
    },
    phase() {
      if (this.discoveryState === 'searching' && !this.status.device) return 'searching'
      if (this.status.control === 'connecting') return 'connecting'
      if (this.status.control === 'reconnecting') return 'reconnecting'
      if (this.status.control === 'connected' && !this.connected) return 'degraded'
      if (this.connected && !this.terminalFresh) return 'degraded'
      if (this.connected) return 'online'
      return 'offline'
    },
    phaseLabel() {
      return {
        searching: '发现中',
        connecting: '连接中',
        reconnecting: '重连中',
        degraded: '链路在线 / 状态延迟',
        online: '终端在线',
        offline: '终端离线',
      }[this.phase]
    },
    endpointLabel() {
      const device = this.status.device || {}
      const name = device.name || device.hostname || this.terminal.device_id || '等待局域网发现'
      const ip = device.ip || device.host
      return ip ? `${name} · ${ip}` : name
    },
    gestureHealthy() {
      const backendReady = Boolean(this.terminal.gesture && this.terminal.gesture.running)
      const frameFresh = Boolean(this.freshness.gesture && this.freshness.gesture.fresh)
      return this.connected && (backendReady || frameFresh)
    },
    gestureEnabled() {
      return Boolean(this.terminal.gesture && this.terminal.gesture.control_enabled)
    },
    gestureLabel() {
      if (!this.connected) return '等待终端'
      if (!this.gestureHealthy) return '识别流未就绪'
      if (this.gestureEnabled) return '控制已激活'
      return '就绪 · 竖拇指激活'
    },
    audioActive() {
      return this.status.audio === 'connected'
    },
    voiceHealthy() {
      const captureReady = Boolean(this.terminal.audio_capture && this.terminal.audio_capture.running)
      return this.connected && (captureReady || this.audioActive)
    },
    voiceLabel() {
      if (!this.connected) return '等待终端'
      if (this.audioActive) return '麦克风通道工作中'
      if (this.voiceHealthy) return '就绪 · 按需启用'
      return '音频采集未就绪'
    },
    terminalLabel() {
      if (!this.connected) return '控制链路断开'
      if (!this.terminalFresh) return '等待实时遥测'
      return `协议 ${this.terminal.protocol_version || '2.0'} · ${this.terminal.mode || 'gesture'}`
    },
    cameraLabel() {
      const camera = this.terminal.camera || {}
      return camera.running ? `在线 #${camera.camera_id ?? 0}` : '未就绪'
    },
    gestureFps() {
      const fps = this.terminal.gesture && Number(this.terminal.gesture.fps)
      return Number.isFinite(fps) && fps > 0 ? `${fps.toFixed(1)} FPS` : '-- FPS'
    },
    npuLabel() {
      const values = Array.isArray(this.terminal.npu_percent)
        ? this.terminal.npu_percent.filter(value => Number.isFinite(Number(value))).map(Number)
        : []
      if (!values.length) return '--'
      return `${Math.max(...values).toFixed(0)}%`
    },
    uptimeLabel() {
      const seconds = Number(this.terminal.uptime_s)
      if (!Number.isFinite(seconds)) return '--'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return hours ? `${hours}h ${minutes}m` : `${minutes}m`
    },
    shortBuildCommit() {
      const commit = String(this.buildInfo.gitCommit || this.buildInfo.commit || 'unknown')
      return commit === 'unknown' ? commit : commit.slice(0, 12)
    },
    errorLabel() {
      if (this.discoveryState === 'searching') return '正在扫描局域网中的 Rock5A…'
      if (this.discoveryError === 'helper_unavailable') return '本机发现助手未启动'
      return this.discoveryError || '尚未发现可用终端'
    },
  },
  mounted() {
    window.addEventListener('terminal-status-open', this.expand)
  },
  beforeDestroy() {
    window.removeEventListener('terminal-status-open', this.expand)
  },
  methods: {
    expand() {
      this.collapsed = false
    },
  },
}
</script>

<style scoped>
.terminal-dock {
  position: relative;
  width: min(340px, 100%);
  color: #ecfbff;
  border: 1px solid rgba(87, 213, 236, 0.28);
  border-radius: 14px;
  background: linear-gradient(145deg, rgba(5, 18, 30, 0.94), rgba(7, 33, 40, 0.88));
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.38), inset 0 1px rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(16px);
  overflow: hidden;
}

.terminal-dock.is-online { border-color: rgba(52, 211, 153, 0.42); }
.terminal-dock.is-degraded,
.terminal-dock.is-reconnecting { border-color: rgba(250, 204, 21, 0.42); }
.terminal-dock.is-offline { border-color: rgba(248, 113, 113, 0.32); }
.terminal-dock.collapsed { width: min(286px, 100%); }

.build-version {
  margin-top: 8px;
  color: rgba(196, 231, 239, 0.72);
  font-size: 11px;
  letter-spacing: 0.02em;
}

.dock-header {
  width: 100%;
  min-height: 54px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.terminal-icon {
  flex: 0 0 32px;
  height: 32px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  padding: 6px;
  border-radius: 10px;
  background: rgba(34, 211, 238, 0.11);
  border: 1px solid rgba(34, 211, 238, 0.22);
}

.terminal-icon span {
  width: 4px;
  border-radius: 4px;
  background: #67e8f9;
  box-shadow: 0 0 8px rgba(103, 232, 249, 0.7);
}
.terminal-icon span:nth-child(1) { height: 9px; }
.terminal-icon span:nth-child(2) { height: 18px; }
.terminal-icon span:nth-child(3) { height: 13px; }

.terminal-heading {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.terminal-heading strong { font-size: 14px; letter-spacing: 0.4px; }
.terminal-heading small {
  max-width: 175px;
  overflow: hidden;
  color: rgba(220, 245, 250, 0.56);
  font-size: 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.phase-badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 7px;
  border-radius: 999px;
  color: #a5f3fc;
  background: rgba(34, 211, 238, 0.1);
  font-size: 10px;
  white-space: nowrap;
}
.phase-badge i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 7px currentColor;
}
.is-online .phase-badge { color: #86efac; background: rgba(134, 239, 172, 0.1); }
.is-degraded .phase-badge,
.is-reconnecting .phase-badge { color: #fde047; background: rgba(253, 224, 71, 0.1); }
.is-offline .phase-badge { color: #fca5a5; background: rgba(252, 165, 165, 0.1); }
.collapse-mark { color: rgba(255, 255, 255, 0.42); font-size: 15px; }

.dock-body {
  padding: 0 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.capability-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  padding-top: 10px;
}
.capability {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.capability-dot {
  flex: 0 0 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.24);
}
.capability.healthy .capability-dot { background: #5eead4; box-shadow: 0 0 7px rgba(94, 234, 212, 0.7); }
.capability.active { border-color: rgba(34, 211, 238, 0.32); background: rgba(34, 211, 238, 0.08); }
.capability div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.capability strong { font-size: 12px; }
.capability small {
  overflow: hidden;
  color: rgba(226, 248, 252, 0.52);
  font-size: 9px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.metric-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  margin-top: 8px;
  color: rgba(225, 247, 250, 0.48);
  font-size: 9px;
}
.metric-row span { display: flex; flex-direction: column; gap: 2px; padding: 5px 6px; }
.metric-row b { color: rgba(236, 253, 255, 0.86); font-size: 10px; font-weight: 500; }

.interaction-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 0 0;
  padding: 7px 9px;
  border-radius: 8px;
  color: rgba(207, 250, 254, 0.7);
  background: rgba(8, 145, 178, 0.09);
  font-size: 9px;
}
.interaction-tip span + span::before { content: '·'; margin-right: 8px; color: rgba(103, 232, 249, 0.35); }

.offline-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 9px;
  color: rgba(255, 255, 255, 0.52);
  font-size: 10px;
}
.offline-actions button {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid rgba(103, 232, 249, 0.3);
  border-radius: 7px;
  color: #a5f3fc;
  background: rgba(34, 211, 238, 0.09);
  cursor: pointer;
}
.offline-actions button:disabled { cursor: wait; opacity: 0.55; }

.dock-expand-enter-active,
.dock-expand-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.dock-expand-enter,
.dock-expand-leave-to { opacity: 0; transform: translateY(-4px); }

@media (max-width: 760px) {
  .terminal-dock { width: min(340px, 100%); }
  .terminal-dock.collapsed { width: min(286px, 100%); }
}
</style>
